"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NetRef = exports.NetRefCtx = void 0;
exports.isNetRef = isNetRef;
class NetRefCtx {
    constructor() {
        this.uniqueRefNo = 0;
        this.map = new Map();
        this.oldRefs = new Set();
        this.handlers = {};
    }
    existingOrNewRef(value) {
        if (value instanceof NetRef || typeof value !== 'string') {
            value = value.valueOf();
        }
        if (!this.map.has(value)) {
            this.map.set(value, new NetRef(this, value));
        }
        return this.map.get(value);
    }
    setRef(oldRef, newRef) {
        this.map.set(oldRef.valueOf(), newRef);
        if (oldRef.valueOf() !== newRef.valueOf()) {
            this.oldRefs.add(oldRef);
            this.map.set(newRef.valueOf(), newRef);
        }
    }
    clearOldRefs() {
        for (const ref of this.oldRefs) {
            this.map.delete(ref);
        }
        this.oldRefs.clear();
    }
    on(evt, handler) {
        if (!this.handlers[evt]) {
            this.handlers[evt] = [];
        }
        this.handlers[evt].push(handler);
    }
    off(evt, handler) {
        if (!this.handlers[evt]) {
            return;
        }
        this.handlers[evt] = this.handlers[evt].filter((h) => h !== handler);
    }
    getUniqueRefNo() {
        return this.uniqueRefNo++;
    }
}
exports.NetRefCtx = NetRefCtx;
class NetRef extends String {
    constructor(ctx, value) {
        super();
        this.ctx = ctx;
        this.handlers = {};
        const self = this;
        this.internalRef = value instanceof NetRef ? value.internalRef.valueOf() : value;
        this.ctx?.setRef(this.internalRef, this);
        return new Proxy(this, {
            get(target, prop) {
                if (typeof prop === 'symbol') {
                    return target[prop];
                }
                if (Number(prop) == prop && !(prop in target)) {
                    return self.internalRef[prop];
                }
                return target[prop];
            },
        });
    }
    on(evt, handler) {
        if (!this.handlers[evt]) {
            this.handlers[evt] = [];
        }
        this.handlers[evt].push(handler);
    }
    off(evt, handler) {
        if (!this.handlers[evt]) {
            return;
        }
        this.handlers[evt] = this.handlers[evt].filter((h) => h !== handler);
    }
    setName(newVal) {
        if (newVal instanceof NetRef) {
            newVal = newVal.internalRef;
        }
        const oldVal = this.internalRef;
        if (oldVal === newVal) {
            return;
        }
        this.internalRef = newVal;
        this.ctx?.setRef(oldVal, this);
        if (this.handlers['change']) {
            for (const handler of this.handlers['change']) {
                handler(this);
            }
        }
    }
    toString() {
        return this.internalRef.valueOf();
    }
    strCast() {
        return this;
    }
    toJSON() {
        return this.internalRef.valueOf();
    }
    valueOf() {
        return this.internalRef.valueOf();
    }
    [Symbol.toPrimitive](_hint) {
        return this.internalRef.valueOf();
    }
    get [Symbol.toStringTag]() {
        return `NetRef (${this.internalRef.valueOf()})`;
    }
    static fromString(ctx, value) {
        if (value instanceof NetRef) {
            return value;
        }
        if (!ctx) {
            return new NetRef(undefined, value);
        }
        return ctx.existingOrNewRef(value);
    }
    at(index) {
        return this.internalRef[index];
    }
    get length() {
        return this.internalRef.length;
    }
    charAt(index) {
        return this.internalRef.charAt(index);
    }
    charCodeAt(index) {
        return this.internalRef.charCodeAt(index);
    }
    concat(...strings) {
        return this.internalRef.concat(...strings);
    }
    includes(searchString, position) {
        return this.internalRef.includes(searchString, position);
    }
    endsWith(searchString, length) {
        return this.internalRef.endsWith(searchString, length);
    }
    indexOf(searchString, position) {
        return this.internalRef.indexOf(searchString, position);
    }
    lastIndexOf(searchString, position) {
        return this.internalRef.lastIndexOf(searchString, position);
    }
    localeCompare(that, locales, options) {
        return this.internalRef.localeCompare(that, locales, options);
    }
    match(regexp) {
        return this.internalRef.match(regexp);
    }
    matchAll(regexp) {
        return this.internalRef.matchAll(regexp);
    }
    normalize(form) {
        return this.internalRef.normalize(form);
    }
    padEnd(targetLength, padString) {
        return this.internalRef.padEnd(targetLength, padString);
    }
    padStart(targetLength, padString) {
        return this.internalRef.padStart(targetLength, padString);
    }
    repeat(count) {
        return this.internalRef.repeat(count);
    }
    replace(...args) {
        return this.internalRef.replace(...args);
    }
    search(regexp) {
        return this.internalRef.search(regexp);
    }
    slice(start, end) {
        return this.internalRef.slice(start, end);
    }
    split(separator, limit) {
        return this.internalRef.split(separator, limit);
    }
    startsWith(searchString, position) {
        return this.internalRef.startsWith(searchString, position);
    }
    substring(start, end) {
        return this.internalRef.substring(start, end);
    }
    toLocaleLowerCase(locales) {
        return this.internalRef.toLocaleLowerCase(locales);
    }
    toLocaleUpperCase(locales) {
        return this.internalRef.toLocaleUpperCase(locales);
    }
    toLowerCase() {
        return this.internalRef.toLowerCase();
    }
    toUpperCase() {
        return this.internalRef.toUpperCase();
    }
    trim() {
        return this.internalRef.trim();
    }
    trimEnd() {
        return this.internalRef.trimEnd();
    }
    trimStart() {
        return this.internalRef.trimStart();
    }
    [Symbol.iterator]() {
        return this.internalRef[Symbol.iterator]();
    }
}
exports.NetRef = NetRef;
function isNetRef(value) {
    return typeof value === 'object' && value instanceof NetRef;
}
//# sourceMappingURL=NetRef.js.map